/**
 * The parity problem, written out.
 *
 * The interesting part is not the solution, it is the sequence: two design
 * goals close every standard option in turn, and the third card is where the
 * escape hatches run out.
 */

export type RuledOut = {
  /** The connective beat that forces this option onto the table. */
  because: string;
  name: string;
  /** How the technique gets parity when it works. */
  how: string;
  /** Why it was not available here. */
  blocker: string;
  /** The one-line verdict. */
  verdict: string;
};

export const PARITY = {
  eyebrow: "The hard part",
  heading: "Two design goals closed every standard answer in turn",
  lede: "Parity is the problem underneath a lot of multiplayer games: every machine has to agree on the same world, and there are three usual ways to get there. I wanted no input delay and I wanted an absurd number of enemies, and between them those two goals ruled out all three. Working out what to do instead is the part of this project I would most want to be asked about.",

  /**
   * The write-up below the lede is the longest block on the site. It sits
   * behind a disclosure so the case study stays skimmable, and the label
   * states what is inside rather than saying "read more": nobody opens a
   * shrug, but they will open a specific promise.
   */
  moreLabel: "Read the full write-up",
  moreHint:
    "Three standard techniques ruled out in turn, why this engine, what got built instead, and the seven ways it could have quietly diverged.",

  ruledOut: [
    {
      because: "The default answer",
      name: "Deterministic lockstep",
      how: "Every peer simulates identical inputs in identical order, so there is nothing to disagree about. Nobody sends positions at all, only inputs.",
      blocker:
        "It gates every tick on every player's input arriving. In a game where you are running around placing walls while a swarm closes in, making your own keystrokes wait on the slowest connection in the session is not a trade worth making. It also needs the simulation to be bit-identical on every machine, which no general-purpose engine gives you for free.",
      verdict: "Correct, and it feels wrong to play.",
    },
    {
      because: "So the standard fix for that",
      name: "Lockstep with rollback",
      how: "Predict ahead locally so input is instant, and when a remote input turns up late, rewind to the last agreed state and resimulate. This is how fighting games feel instant across an ocean.",
      blocker:
        "Rollback is priced on the cost of snapshotting and resimulating the world, and it happens constantly. Here the world is thousands of entities, a navigation grid, a live flow field and thousands of projectiles. One rollback costs more than the frame it was meant to rescue.",
      verdict: "Affordable for two fighters, not for ten thousand.",
    },
    {
      because: "So give up prediction entirely",
      name: "Server-driven everything",
      how: "The server owns every position and replicates them all. No prediction, no divergence, no parity problem left to solve.",
      blocker:
        "That is one replicated object per enemy, roughly 13.5 bytes per moving object per frame, per client, multiplied by the host's upload. The transport runs out somewhere in the low hundreds. The game would be perfectly correct and would never reach the scale that makes it the game.",
      verdict: "Correct, and it never gets to be Swarmhold.",
    },
  ] satisfies RuledOut[],

  /** Why this engine, given that its weakness was already priced in. */
  engine: {
    eyebrow: "Why Photon Fusion, then",
    body: "Fusion is a state-authority framework, not a deterministic one. Photon sells a deterministic engine as well, and on paper that looks like the answer to a parity problem, but it would have bought me nothing: lockstep was already off the table for reasons that had nothing to do with determinism. Fusion gives the half I actually needed, which is authoritative state replication with real interpolation and prediction for the things a player feels most, which is movement, abilities and combat, plus a hosted relay so a solo developer ships without running servers. It leaves the crowd unsolved, and the crowd is the part I wanted to build anyway.",
  },

  /** The turn. */
  pivot: {
    eyebrow: "At which point most projects settle",
    body: "The normal move here is to give one thing up. Fewer enemies, or accepted input delay, or a smaller battlefield. There is a fourth option, which is to pay for it: that last ceiling is a budget rather than a law, and dedicated servers with the bandwidth and hosting spend to match would move it a long way up. Studios with that money do exactly this. All four are reasonable and most projects take one of them. I wanted none of them. Being surrounded from every direction is the entire game, a version with two hundred enemies and a quarter second of input lag is a different and worse game, and a solo developer signing up for a server bill that scales with player count is a studio that closes. None of the standard answers were going to work. So I got scrappy: define the constraints myself, measure against them, and build what the measurements allowed.",
  },

  /**
   * The run-up into the answer. Prose rather than stat tiles: a document count
   * proves nothing, and the interesting part is what the measuring turned up.
   */
  cost: {
    line: [
      "It took months, and most of that time went into instrumentation rather than gameplay. Photon does not publish the numbers that decide any of this, so every budget in the architecture is one I had to measure myself: what the reliable channel actually delivers per second, where the snapshot channel starts to give, what a thousand enemies dying in the same tick costs on the wire.",
      "Some of what I concluded early on I later replaced. The ceiling I had spent weeks designing around turned out not to be a bandwidth limit at all: it was a message-rate limit that had been misread as one, and the moment that was clear, a whole class of designs I had already ruled out came back onto the table. Countless measurements and a lot of failed playtests later, this is where it landed.",
    ],
    handoff: "What I ended up with:",
  },

  answerEyebrow: "So how does it work",
  answerHeading: "Stop buying one answer for everything",
  answer: [
    "The mistake was looking for a single technique. Parity is not one property the whole world needs equally: it is a cost, and different entities deserve different amounts of it. So the world is split into tiers and each tier pays the price its role justifies.",
    "Anything a player can be killed by, aims at, or blames for their death is server-authoritative and replicated properly. That is a small population, and it can afford the bandwidth precisely because everything else stopped asking for any.",
    "The bulk of the crowd runs the same simulation on every peer, driven from replicated inputs alone: the tick, the goals, the spawn events. This is not lockstep, because nobody waits for anybody. Every peer reaches the same answer because the inputs are identical and so is the code.",
    "All three of the ruled out answers come back here, each scoped to the tier that can afford it. Authority for the entities a player can lose to. Prediction, corrected against that authority, for what sits under the player's own hand. Determinism from shared inputs for everything else, without the barrier that made lockstep unaffordable. What none of them survived was being the answer for the whole world at once.",
    "The point of all of it is that none of it is visible. Your movement answers the moment you press a key, abilities fire instantly, combat resolves where you expect, and the horde reads as one thing rather than ten thousand arguments about where it is. That holds whether you are hosting or joining, which is the part most games quietly concede: the non-host usually eats the latency. Here they are held to the same bar as the host, and that commitment is why prediction is load-bearing rather than an optimization.",
  ],

  guardsEyebrow: "Which only works if divergence is engineered out",
  guards: [
    {
      name: "Simulation versus render phase",
      fix: "Parity-sensitive logic reads replicated state in the simulation step, never interpolated state in the render step.",
    },
    {
      name: "Tick-phase offset",
      fix: "Peers sit on different ticks at the same wall-clock instant, so anything that must agree is keyed to the tick.",
    },
    {
      name: "Spawn timing",
      fix: "Clients see spawns one or two ticks after the host; logic that reads brand-new state is guarded across that window.",
    },
    {
      name: "Float knife-edges",
      fix: "A comparison like distance < radius can flip on a rounding difference, so load-bearing checks use epsilon bands or hysteresis.",
    },
    {
      name: "Compounding drift",
      fix: "Small per-tick differences feed the next tick on predicted tiers, so corrections are scoped to the tier that can drift.",
    },
    {
      name: "Burst float mode",
      fix: "Fast math is not reproducible across machines. Parity-sensitive jobs are pinned to deterministic float mode.",
    },
    {
      name: "Wall-clock as input",
      fix: "No gameplay decision reads real time or frame count. Seeds come from world seed, tick and entity id.",
    },
  ],

  standard: {
    label: "The standard is imperceptible, not perfect",
    body: "A wall you placed showing up one tick later on your friend's screen is fine, and chasing it would cost real budget. An enemy killing you on the host while you were alive on your own screen is a critical failure. Knowing which is which is the whole job.",
  },
};
